// TODO: The components in this file can be extracted out in components of their own
// and imported back in this file, that would be the right way to fix this
/* eslint-disable @typescript-eslint/no-use-before-define */
import { FC, lazy, Suspense, useState } from 'react';
import { ApolloCache, useMutation, useQuery } from '@apollo/client';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import moment from 'moment-timezone';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';
import { Range } from 'common/types';
import { HindsightDataResolver } from 'common/graphqlTypes';
import { getDurationText } from 'common/util/time';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';

import { activeTeamState } from 'components/account/account.state';
import { formatBillingDate } from 'components/billingAndUsage/utils';

import Page from 'components/common/Page/Page';
import PageHeader from 'components/common/PageHeader/PageHeader';
import Link from 'components/common/Link/Link';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import { ButtonVariant } from 'components/common/Button/Button';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import Card from 'components/common/Card/Card';

import {
  BillingPlan,
  CostManagementTableWrapper,
  Filter,
  GraphDetails,
  Hindsight,
  NotificationCard,
} from 'components/billingAndUsage/components/';

import {
  BILLING_NOTIFICATIONS,
  GET_BILLING_AND_USAGE_DATA,
  GET_HINDSIGHT_DATA,
  GET_VOLUMES,
  UPDATE_BUDGET_NOTIFICATIONS,
  UPDATE_CREDIT_CARD,
  UPDATE_OVERAGE_NOTIFICATIONS,
} from 'components/billingAndUsage/queries';

import { ACCOUNT } from 'components/common/CreditCardDialog/queries';

import {
  AccountDataType,
  BillingAndUsageResponseData,
  BudgetCategoryType,
  CreditCardData,
  NotificationResponse,
  NotificationsType,
  PlanDataType,
  TimeVariables,
  VolumesData,
} from 'components/billingAndUsage/types';

import { getFirstDayOfMonth, getNextHour, getPreviousDay } from 'common/util/time';

import './BillingAndUsagePage.scss';

import { DocumentNode } from 'graphql';
import produce from 'immer';
import merge from 'lodash/merge';
import { isNumber } from 'lodash';

const defaultAccount: AccountDataType = {
  isCustomPlan: false,
  planData: { gbPerDay: 0, price: 0, retention: 30 },
  planDataForNextBillingCycle: { gbPerDay: 0, price: 0, retention: 30 },
  hasCreditCard: false,
  isLogOverageNotificationsWidgetEnabled: false,
  canChangeBillingPlan: false,
  email: '',
  isMasterWithSubsidiaries: false,
  billingType: null,
};

const CreditCardDialog = lazy(() => import('components/common/CreditCardDialog/CreditCardDialog'));

/**
 * Page for providing billing information and optional cost management.
 *
 * It shows plan details at the top: data volume, retention, and cost. Below that is a graph if recent volume.
 * You can also receive email notifications if you exceed your planned volume - either overall or for a single
 * category - over the past 24 hours. At the bottom are two panels of general information about features and plans.
 */
const BillingAndUsagePage: FC = () => {
  const [showBillingPlan, setShowBillingPlan] = useState(false);
  const [showCreditCardForm, setShowCreditCardForm] = useState(false);

  const { data: billingAndUsageData, loading: billingAndUsageDataLoading } =
    useQuery<BillingAndUsageResponseData>(GET_BILLING_AND_USAGE_DATA);

  const [time] = useState<TimeVariables>({
    today: moment().toISOString(),
    yesterday: moment().subtract(1, 'day').toISOString(),
    firstDayOfMonth: getFirstDayOfMonth(),
  });

  const { data: volumesData } = useQuery<VolumesData>(GET_VOLUMES, {
    variables: { today: time.today, yesterday: time.yesterday, firstDayOfMonth: time.firstDayOfMonth },
    fetchPolicy: 'no-cache',
  });
  const isHindsightLabEnabled = useIsLabEnabled(K.LAB.HINDSIGHT);
  const hindsightStartDate = getPreviousDay(30);
  // set end to next hour cutoff so we get everything (without a render/request loop)
  const hindsightEndDate = getNextHour();
  const { data } = useQuery(GET_HINDSIGHT_DATA, {
    skip: !isHindsightLabEnabled,
    variables: { startDate: hindsightStartDate, endDate: hindsightEndDate },
  });
  const hindsightData: HindsightDataResolver = data?.hindsightData;

  if (billingAndUsageDataLoading) {
    return <LoadingPlaceholder />;
  }

  const account = billingAndUsageData?.account || defaultAccount;

  const handleOpenBillingPlan = (): void => {
    setShowBillingPlan(!showBillingPlan);
  };

  const handleOpenCreditCardForm = (): void => {
    if (account.canChangeBillingPlan) {
      setShowCreditCardForm(!showCreditCardForm);
    }
  };

  const updatePlanButtons = account.canChangeBillingPlan
    ? [
        {
          name: 'Change Plan',
          onClick: handleOpenBillingPlan,
          variant: ButtonVariant.SECONDARY,
        },
        {
          name: 'Update Credit Card',
          onClick: handleOpenCreditCardForm,
          variant: ButtonVariant.SECONDARY,
        },
      ]
    : [];

  return (
    <Page
      docTitle="Billing Plans"
      pageModifier="CurrentBillingSettings__Page Page--Standard"
      testId={ELEMENT.billingPage}
    >
      <PageHeader title="Billing" additionalRightButtons={updatePlanButtons} />
      {account.planDataForNextBillingCycle && (
        <BillingPlanUpdateNotification planDataForNextBillingCycle={account.planDataForNextBillingCycle} />
      )}
      {billingAndUsageData && (
        <BillingData
          billingAndUsageData={billingAndUsageData}
          volumesData={volumesData}
          hindsightData={hindsightData}
          hindsightStartDate={hindsightStartDate}
          hindsightEndDate={hindsightEndDate}
          showBillingPlan={showBillingPlan}
          showCreditCardForm={showCreditCardForm}
          handleOpenBillingPlan={handleOpenBillingPlan}
          handleOpenCreditCardForm={handleOpenCreditCardForm}
        />
      )}
      <FeatureList />
      <BillingNotes />
    </Page>
  );
};

const BillingPlanUpdateNotification: FC<{
  planDataForNextBillingCycle: { gbPerDay: number; price: number; retention: number };
}> = ({ planDataForNextBillingCycle }) => (
  <Card className="CurrentBillingSettings__Card CurrentBillingSettings__BillingUpdate">
    <div>
      <InfoOutlinedIcon className="CurrentBillingSettings__BillingUpdate__InfoIcon" />
      You recently changed your billing plan. Changes will take effect next month.
    </div>
    <ul className="CurrentBillingSettings__BillingUpdate__Info">
      <li>Plan: {planDataForNextBillingCycle.gbPerDay} GB/day</li>
      <li>Price: ${planDataForNextBillingCycle.price} per month</li>
    </ul>
  </Card>
);

/**
 * Show plan details, a usage graph, and cost management (if enabled). Handle editing of credit card information.
 */
const BillingData: FC<{
  billingAndUsageData: BillingAndUsageResponseData;
  volumesData?: VolumesData;
  hindsightData?: HindsightDataResolver;
  hindsightStartDate: string;
  hindsightEndDate: string;
  showBillingPlan: boolean;
  showCreditCardForm: boolean;
  handleOpenBillingPlan: () => void;
  handleOpenCreditCardForm: () => void;
}> = ({
  billingAndUsageData,
  volumesData,
  hindsightData,
  hindsightStartDate,
  hindsightEndDate,
  showBillingPlan,
  showCreditCardForm,
  handleOpenBillingPlan,
  handleOpenCreditCardForm,
}) => {
  const account = billingAndUsageData?.account || defaultAccount;
  const budgetCategories = billingAndUsageData?.budgetCategories || ([] as BudgetCategoryType[]);
  const timezone = useRecoilValue(timezoneState);
  const [dateRange, setDateRange] = useState({ min: 0, max: 0 } as Range);

  let dateRangeEl = null;
  if (dateRange.min > 0 && dateRange.max > dateRange.min) {
    const startDate = formatBillingDate(dateRange.min, timezone);
    const endDate = formatBillingDate(dateRange.max, timezone);
    dateRangeEl = (
      <div className="CurrentBillingSettings__UsageGraph__Header__Range">{`${startDate} - ${endDate}`}</div>
    );
  }
  const usageGraphHeader = (
    <div className="CurrentBillingSettings__SplitHeader">
      <div>Recent Log Volume</div>
      {dateRangeEl}
    </div>
  );

  return (
    <>
      {!showBillingPlan && (
        <BillingSettings
          plan={account.planData}
          billingType={account.billingType}
          hindsightRetentionDays={hindsightData?.retentionDays || 0}
        />
      )}
      {showBillingPlan && (
        <BillingPlan
          isCreditCard={account.hasCreditCard}
          isCustomPlan={account.isCustomPlan}
          email={account.email}
          onClose={handleOpenBillingPlan}
          onAddCreditCard={handleOpenCreditCardForm}
          hasCreditCard={account.hasCreditCard}
        />
      )}
      <Card
        header={usageGraphHeader}
        className="CurrentBillingSettings__Card"
        headerClass="CurrentBillingSettings__Header"
      >
        <GraphDetails
          usagePerDay={account.planData.gbPerDay}
          handleDateRange={setDateRange}
          retentionDays={account.planData.retention}
          isMasterWithSubsidiaries={account.isMasterWithSubsidiaries}
        />
      </Card>
      {hindsightData && hindsightData?.retentionDays > 0 && (
        <Hindsight
          hindsightData={hindsightData as HindsightDataResolver}
          startDate={hindsightStartDate}
          endDate={hindsightEndDate}
        />
      )}
      <>
        <CostManagement
          data={budgetCategories}
          volumesData={volumesData}
          isMasterWithSubsidiaries={account.isMasterWithSubsidiaries}
        />
        <CostManagementFilters data={budgetCategories} />
      </>
      {<Notifications isMasterWithSubsidiaries={account.isMasterWithSubsidiaries} />}
      <CreditCard
        hasCreditCard={account.hasCreditCard}
        onClose={handleOpenCreditCardForm}
        showDialog={showCreditCardForm}
      />
    </>
  );
};

type SettingCardProps = {
  header: string;
  currency?: string;
  size: number;
  unit: string;
};

/**
 * A small panel that displays a single bit of plan information.
 *
 * @prop header      descriptive title
 * @prop currency    currency symbol that comes before size
 * @prop size        number or amount
 * @prop unit        unit or other text that follows size
 */
function SettingCard({ header, currency = '', size, unit }: SettingCardProps): JSX.Element {
  return (
    <div className="CurrentBillingSettings__SettingCard__Item">
      <div className="CurrentBillingSettings__SettingCard__Header">{header}</div>
      <div>
        <span className="CurrentBillingSettings__SettingCard__Size">{currency}</span>
        <span className="CurrentBillingSettings__SettingCard__Size">{size}</span>
        &nbsp;
        <span className="CurrentBillingSettings__SettingCard__Unit">{unit}</span>
      </div>
    </div>
  );
}

type BillingSettingsProps = {
  plan: PlanDataType;
  hindsightRetentionDays: string | number;
  billingType: string | null;
};

/**
 * A row of small panels that show basic plan information.
 *
 * @prop plan              billing plan data object
 * @prop billingType      if 'channel' hide pricing on billing page
 */
function BillingSettings({ plan, hindsightRetentionDays, billingType }: BillingSettingsProps): JSX.Element {
  // Handle Hindsight retention as either a number (of days) or a duration
  let retention = 0;
  if (isNumber(hindsightRetentionDays)) {
    retention = hindsightRetentionDays;
  } else {
    const durationText = getDurationText(hindsightRetentionDays, K.DATE.DAY);
    retention = Number(durationText.split(' ')[0]);
  }

  return (
    <div className="CurrentBillingSettings__SettingCard__Container" data-test-id={ELEMENT.planPageBillingSettings}>
      <SettingCard header="Plan" size={plan.gbPerDay} unit="GB/day" />
      <SettingCard header="Basic Retention" size={plan.retention} unit="days" />
      {billingType !== K.BILLING_TYPE.CHANNEL && (
        <SettingCard header="Price" currency="$" size={plan.price} unit="per month" />
      )}
      {retention > 0 && <SettingCard header="Hindsight" size={retention} unit="days" />}
    </div>
  );
}

type CreditCardProps = {
  showDialog: boolean;
  onClose: () => void;
  hasCreditCard: boolean;
};

/**
 * Dialog that lets the user edit their credit card details.
 *
 * @prop showDialog       if true, show this dialog
 * @prop hasCreditCard    if true, user has credit card on file
 * @prop onClose          function to run when dialog closes
 */
function CreditCard({ showDialog, hasCreditCard, onClose }: CreditCardProps): JSX.Element {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const showError = (): void => {
    setPageAlertConfig({
      message: 'There was an error while updating credit card info',
      variant: PageAlertVariant.ERROR,
      autoHide: true,
    });
  };

  const showSuccess = (): void => {
    setPageAlertConfig({
      message: 'Credit card info has been updated',
      variant: PageAlertVariant.SUCCESS,
      autoHide: true,
    });
  };

  const [updateCreditCard, { loading }] = useMutation(UPDATE_CREDIT_CARD, {
    onCompleted: () => {
      showSuccess();
    },
    onError: () => {
      showError();
    },
    refetchQueries: [
      {
        query: ACCOUNT,
      },
    ],
  });

  const handleSubmit = (stripeToken: string, billingInformation: CreditCardData): void => {
    updateCreditCard({
      variables: {
        stripeToken,
        billingInformation,
      },
    });
  };

  const title = `${hasCreditCard ? 'Update' : 'Add'} Credit Card`;
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <CreditCardDialog
        dialogTitle={title}
        dialogOpen={showDialog}
        onClose={onClose}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </Suspense>
  );
}

type CostManagementProps = {
  data: BudgetCategoryType[];
  volumesData?: VolumesData;
  isMasterWithSubsidiaries?: boolean;
  email?: string;
};

/**
 * Users create log categories, set planned quotas, and then specify which logfiles or servers to include in each category.
 * Scalyr displays your log volume by category for the past 24 hours, and for the month-to-date.
 * Cost control is provided through the specification of various discard filters. These filters, applied to a log category
 * and expressed in Scalyr query language, identify logs you wish to discard. You will not be charged for discarded logs.
 * Discard filters can be temporarily disabled for a specified number of hours.
 *
 * @prop data                       info about log categories
 * @prop volumesData                volume data for each category
 * @prop isMasterWithSubsidiaries   if true, logged account is master billing account with more subsidiary accounts
 * @prop email                      logged user email
 */
function CostManagement({ data, volumesData, isMasterWithSubsidiaries }: CostManagementProps): JSX.Element {
  const costManagementHeader = (
    <div>
      <span>Cost Management</span>
      <Link className="link" openInNewTab href={HELP_ROUTE.HELP_COST_MANAGEMENT}>
        <ElementWithTooltip tooltip="Help">
          <HelpOutlineOutlinedIcon className="CurrentBillingSettings__Title--Icon" />
        </ElementWithTooltip>
      </Link>
    </div>
  );

  const { emailAddress } = useRecoilValue(activeTeamState);
  return (
    <Card
      header={costManagementHeader}
      className="CurrentBillingSettings__Card"
      headerClass="CurrentBillingSettings__Header"
    >
      {isMasterWithSubsidiaries && <p>Log categories only apply to logs in the {emailAddress} account</p>}
      {volumesData && <CostManagementTableWrapper data={data} volumesData={volumesData} />}
    </Card>
  );
}

type CostManagementFiltersProps = {
  data: BudgetCategoryType[];
};

function CostManagementFilters({ data }: CostManagementFiltersProps): JSX.Element {
  const renderFilters = data.map((item: BudgetCategoryType) => (
    <Filter key={item.id} title={item.name} logFilter={item.logFilter} filters={item.filters} categoryId={item.id} />
  ));
  return (
    <div className="CurrentBillingSettings__Filters" data-test-id={ELEMENT.planPageLogCategoryDiscardFiltersContainer}>
      {renderFilters}
    </div>
  );
}

const queryWriter =
  <Q, M>(query: DocumentNode, write: (prev: Q, res: M) => Q) =>
  (cache: ApolloCache<unknown>, res: { data?: M }) => {
    if (res.data) {
      const prev = cache.readQuery({ query: BILLING_NOTIFICATIONS }) as Q;
      cache.writeQuery({
        query: BILLING_NOTIFICATIONS,
        data: write(prev, res.data),
      });
    }
  };

type NotificationProps = {
  showSubsidiaryLogVolume?: boolean;
  isMasterWithSubsidiaries?: boolean;
};

/**
 * Allows the user to configure email addresses to be notified if log volume exceeds the plan.
 * @prop showSubsidiaryLogVolume    if true, enable show message on card for master accounts - temporary lab to enable subisidiary log volume feature
 * @prop isMasterWithSubsidiaries   if true, logged account is master billing account with subsidiary accounts
 * @prop email                      logged user email
 */

const Notifications: FC<NotificationProps> = ({ isMasterWithSubsidiaries }: NotificationProps) => {
  const { data: billingNotificationsData, loading } = useQuery(BILLING_NOTIFICATIONS);
  const data = billingNotificationsData?.billingNotifications ?? null;

  const overageDescription = data?.overageNotificationSettings?.dailyThresholdPercent
    ? 'Get notified if your average daily log volume (from the beginning of the month) exceeds this percentage'
    : 'Get notified if your average daily log volume (from the beginning of the month) exceeds this amount';

  const [updateOverageNotification] = useMutation(UPDATE_OVERAGE_NOTIFICATIONS);

  const [updateBudgetNotification] = useMutation(UPDATE_BUDGET_NOTIFICATIONS);

  const { emailAddress } = useRecoilValue(activeTeamState);

  const handleUpdateBudgetNotification = async (
    notification: NotificationResponse,
  ): Promise<NotificationResponse | null> => {
    const budgetNotificationRequest = {
      disabled: notification.disabled,
      emails: notification.emails,
    };
    const { data } = await updateBudgetNotification({
      variables: { budgetNotificationRequest },
      update: queryWriter<
        { billingNotifications: NotificationsType },
        { updateBudgetNotification: Omit<NotificationsType['budgetNotification'], 'accountId'> }
      >(BILLING_NOTIFICATIONS, (prev, res) =>
        produce(prev, (draftState) => {
          merge(draftState.billingNotifications.budgetNotification, res.updateBudgetNotification);
        }),
      ),
    });
    return data?.updateBudgetNotification;
  };

  const handleUpdateOverageNotification = async (
    notification: NotificationResponse,
  ): Promise<NotificationResponse | null> => {
    const overageNotificationSettingsRequest = {
      disabled: notification.disabled,
      emails: notification.emails,
      threshold: notification.dailyThresholdGib,
      thresholdPercent: notification.dailyThresholdPercent,
    };
    const { data } = await updateOverageNotification({
      variables: { overageNotificationSettingsRequest },
      update: queryWriter<
        { billingNotifications: NotificationsType },
        { updateOverageNotificationSettings: NotificationsType['overageNotificationSettings'] }
      >(BILLING_NOTIFICATIONS, (prev, res) =>
        produce(prev, (draftState) => {
          merge(draftState.billingNotifications.overageNotificationSettings, res.updateOverageNotificationSettings);
        }),
      ),
    });
    return data?.updateOverageNotificationSettings;
  };

  if (loading) {
    return <LoadingPlaceholder />;
  }

  if (!data) {
    return null;
  }

  return (
    <Card header="Notifications" className="CurrentBillingSettings__Card" headerClass="CurrentBillingSettings__Header">
      {isMasterWithSubsidiaries && <p>Log notifications only apply to logs in the {emailAddress} account</p>}
      <div className="CurrentBillingSettings__Notifications">
        <NotificationCard
          title="LOG OVERAGE"
          description={overageDescription}
          notification={data.overageNotificationSettings}
          onSubmit={handleUpdateOverageNotification}
          showDailyThresholdGib
          indicateOwnerIsDefault
          testIdContainer={ELEMENT.planPageLogOverageNotificationContainer}
          testIdSwitch={ELEMENT.planPageLogOverageNotificationSwitchOnOff}
          testIdEmailInput={ELEMENT.planPageLogOverageNotificationEmailInput}
          testIdSizeInput={ELEMENT.planPageLogOverageNotificationSizeInput}
          testIdSaveButton={ELEMENT.planPageLogOverageNotificationSaveButton}
        />
        <NotificationCard
          title="LOG CATEGORY NOTIFICATION"
          description="Get notified if any category exceeds its plan for one day"
          notification={data.budgetNotification}
          onSubmit={handleUpdateBudgetNotification}
          showDailyThresholdGib={false}
          testIdContainer={ELEMENT.planPageLogCategoryNotificationContainer}
          testIdSwitch={ELEMENT.planPageLogCategoryNotificationSwitchOnOff}
          testIdEmailInput={ELEMENT.planPageLogCategoryNotificationEmailInput}
          testIdSaveButton={ELEMENT.planPageLogCategoryNotificationSaveButton}
        />
      </div>
    </Card>
  );
};

/**
 * Static list of features included with the user's plan.
 */
function FeatureList(): JSX.Element {
  return (
    <Card
      header="What's Included"
      className="CurrentBillingSettings__Card"
      headerClass="CurrentBillingSettings__Header"
    >
      <div className="CurrentBillingSettings__FeatureList">
        <ul className="CurrentBillingSettings__FeatureList--Items">
          <li>
            <p>Log Collection</p>
          </li>
          <li>
            <p>Custom Log Parsing</p>
          </li>
          <li>
            <p>Log Graphs</p>
          </li>
          <li>
            <p>Server Monitoring</p>
          </li>
          <li>
            <p>Multiple Users</p>
          </li>
        </ul>
        <ul className="CurrentBillingSettings__FeatureList--Items">
          <li>
            <p>Audit Trails</p>
          </li>
          <li>
            <p>Custom Dashboards</p>
          </li>
          <li>
            <p>Log Search & Analysis</p>
          </li>
          <li>
            <p>Custom Alerts</p>
          </li>
          <li>
            <p>Custom Metrics</p>
          </li>
        </ul>
      </div>
    </Card>
  );
}

/**
 * General information about Scalyr billing plans.
 */
function BillingNotes(): JSX.Element {
  return (
    <Card header="Billing Notes" className="CurrentBillingSettings__Card" headerClass="CurrentBillingSettings__Header">
      <ul className="CurrentBillingSettings__Notes">
        <li>
          <p>When you select a plan, you will be charged pro rata for the remaining days in the current month.</p>
        </li>
        <li>
          <p>Subsequent months are billed in advance, on the first day of the month.</p>
        </li>
        <li>
          <p>Your free trial lasts 30 days from when you begin uploading log data.</p>
        </li>
        <li>
          <p>
            If your average daily log volume over the course of a month exceeds your selected plan, you will be charged
            at the end of the month, based on our regular per-gigabyte rate plus a 10% surcharge. For instance, if you
            have selected 5 GB/day with 30 day retention, but your average volume for the month was 7 GB/day, your next
            month&apos;s bill will include a $110 additional charge (2 GB/day excess at $50 / GB, plus 10% surcharge).
          </p>
        </li>
        <li>
          <p>To cancel your account, please contact Support.</p>
        </li>
      </ul>
    </Card>
  );
}

export default BillingAndUsagePage;
